import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon, UserIcon, PhoneIcon, BriefcaseIcon, IdentificationIcon, DocumentCheckIcon,
  CalendarDaysIcon, MapPinIcon, BuildingOffice2Icon, ShieldCheckIcon, AcademicCapIcon,GlobeAsiaAustraliaIcon,ClipboardDocumentCheckIcon,
  HeartIcon, ArrowDownTrayIcon
} from "@heroicons/react/24/solid";

// ============================
// 🔹 KONFIGURASI FORM (Sumber Kebenaran Tunggal)
// ============================
const FORM_CONFIG = {
  // Pribadi
  tempatLahir: { label: "Tempat Lahir", icon: MapPinIcon },
  tanggalLahir: { label: "Tanggal Lahir", icon: CalendarDaysIcon },
  pendidikanTerakhir: { label: "Pendidikan Terakhir", icon: AcademicCapIcon },
  tinggi: { label: "Tinggi Badan", format: (v) => v ? `${v} cm` : '-' },
  berat: { label: "Berat Badan", format: (v) => v ? `${v} kg` : '-' },
  golonganDarah: { label: "Golongan Darah", icon: HeartIcon },
  alamat: { label: "Alamat Lengkap", icon: MapPinIcon },
  jenisKelamin:{label: "Jenis Kelamin", Icon: UserIcon },
  agama:{label: "Agama", Icon: GlobeAsiaAustraliaIcon },
  // Kontak
  noHp: { label: "Nomor Handphone", icon: PhoneIcon },
  email: { label: "Email Pribadi", icon: UserIcon },
  alamatKantor: { label: "Alamat Kantor", icon: BuildingOffice2Icon },
  noKantor: { label: "Nomor Kantor", icon: PhoneIcon },
  emailPerusahaan: { label: "Email Perusahaan", icon: UserIcon },
  // Lainnya
  npwp: { label: "NPWP", icon: IdentificationIcon },
  noRegister: { label: "Nomor Register", icon: IdentificationIcon },
  bpjsKetenagakerjaan: { label: "BPJS Ketenagakerjaan", icon: ShieldCheckIcon },
  noBpjs: { label: "Nomor BPJS Kesehatan", icon: HeartIcon },
  noDplk: { label: "Nomor DPLK", icon: DocumentCheckIcon },
  diksar: { label: "Status Diksar", icon: ClipboardDocumentCheckIcon },
  rumusSidikJari: { label: "Rumus Sidik Jari", icon: IdentificationIcon },
  fotoIjazahGadaPratama: { label: "Foto Ijazah Gada Pratama", Icon: AcademicCapIcon },
  // Pekerjaan
  namaPerusahaan: { label: "Lokasi Kerja", icon: BuildingOffice2Icon },
  jabatan: { label: "Jabatan", icon: BriefcaseIcon },
  pangkat: { label: "Pangkat / Level", icon: ShieldCheckIcon },
  tmt: { label: "TMT (Tanggal Mulai Tugas)", icon: CalendarDaysIcon },
  // Dokumen
  pasFoto: { label: "Pas Foto Diri" },
  fotoKtp: { label: "Foto KTP" },
  fotoKk: { label: "Foto Kartu Keluarga" },
  fotoIjazah: { label: "Foto Ijazah Terakhir" },
};

const navigationItems = [
  { id: "pribadi", label: "Informasi Pribadi", icon: UserIcon, fields: ["tempatLahir", "tanggalLahir", "domisili", "jenisKelamin", "pendidikanTerakhir","agama",  "tinggi", "berat", "golonganDarah", "alamat"] },
  { id: "kontak", label: "Kontak", icon: PhoneIcon, fields: ["noHp", "email", "alamatKantor", "noKantor", "emailPerusahaan"] },
  { id: "lainnya", label: "Info Lainnya", icon: IdentificationIcon, fields: ["npwp", "noRegister", "bpjsKetenagakerjaan", "noBpjs", "noDplk", "rumusSidikJari"] },
  { id: "pekerjaan", label: "Pekerjaan", icon: ShieldCheckIcon, fields: ["namaPerusahaan", "jabatan", "pangkat", "nip", "tmt"] },
  { id: "dokumen", label: "Dokumen", icon: DocumentCheckIcon, fields: ["pasFoto", "fotoKtp", "fotoKk", "fotoIjazah", "fotoIjazahGadaPratama" ] },
];

const documentFields = ["pasFoto", "fotoKtp", "fotoKk", "fotoIjazah", "fotoIjazahGadaPratama"];

// ============================
// 🔹 KOMPONEN KECIL (UI Components)
// ============================

const DetailItem = ({ fieldKey, value }) => {
  const config = FORM_CONFIG[fieldKey];
  if (!config) return null;
  const displayValue = config.format ? config.format(value) : (value || "-");
  
  return (
    <div className={`py-4 px-6 ${['alamat', 'alamatKantor'].includes(fieldKey) ? 'md:col-span-2' : ''}`}>
        <p className="text-sm font-medium text-slate-500">{config.label}</p>
        <p className="text-base font-semibold text-slate-800 break-words">{displayValue}</p>
    </div>
  );
};

const DocumentCard = ({ fieldKey, fileUrl }) => {
  const config = FORM_CONFIG[fieldKey];
  const hasFile = typeof fileUrl === 'string' && fileUrl.startsWith('http');
  return (
    <div>
      <h4 className="text-sm font-bold text-slate-700 mb-2">{config.label}</h4>
      <a href={hasFile ? fileUrl : undefined} target="_blank" rel="noopener noreferrer" className="block group">
        <div className={`relative aspect-[4/3] rounded-lg flex items-center justify-center border-2 border-dashed transition-all duration-300 ${hasFile ? 'border-slate-300 group-hover:border-teal-500 group-hover:scale-105 bg-slate-100 p-1' : 'border-slate-200 bg-slate-50'}`}>
          {hasFile ? (
            <>
              <img src={fileUrl} alt={config.label} className="w-full h-full object-cover rounded-md" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-md">
                <ArrowDownTrayIcon className="w-8 h-8 text-white" />
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-400">Belum diunggah</p>
          )}
        </div>
      </a>
    </div>
  );
};

const ProfileSidebar = ({ data, activeTab, setActiveTab }) => (
  <aside className="w-full lg:w-[320px] lg:flex-shrink-0 bg-white lg:border-r lg:border-slate-200 p-6 flex flex-col">
    <div className="flex flex-row lg:flex-col items-center text-left lg:text-center gap-5">
      {data.pasFoto ? (
        <img src={data.pasFoto} alt="Pas Foto" className="w-20 h-20 lg:w-32 lg:h-32 rounded-full object-cover ring-4 ring-slate-100 shadow-md"/>
      ) : (
        <div className="w-20 h-20 lg:w-32 lg:h-32 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-5xl font-bold">
          {data.nama?.charAt(0) || "?"}
        </div>
      )}
      <div className="flex-grow">
        <h3 className="text-2xl font-bold text-teal-600">{data.nama || "-"}</h3>
        <p className="text-sm text-slate-500">{data.jabatan || "Jabatan Belum Diisi"}</p>
        <p className="text-sm text-slate-400 font-mono mt-1 lg:mt-2">NIK: {data.nik || "-"}</p>
      </div>
    </div>
    <div className="border-t my-6"></div>
    <nav className="hidden lg:flex flex-col space-y-1.5">
      {navigationItems.map((item) => {
        const isActive = activeTab === item.id;
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${isActive ? "bg-teal-50 text-teal-700" : "text-slate-600 hover:bg-slate-100"}`}
          >
            {isActive && <motion.div layoutId="sidebar-active-indicator" className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 bg-teal-500 rounded-r-full"></motion.div>}
            <Icon className="w-5 h-5 ml-2" />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  </aside>
);

// ============================
// 🔹 Komponen Utama
// ============================
export default function DetailDataDiri({ data, onClose }) {
  const [activeTab, setActiveTab] = useState(navigationItems[0].id);
  if (!data) return null;

  const activeNavItem = navigationItems.find(item => item.id === activeTab);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ ease: "easeInOut", duration: 0.3 }}
        className="bg-slate-50 rounded-xl w-full max-w-6xl shadow-2xl max-h-[95vh] flex flex-col overflow-hidden"
      >
        <header className="flex justify-between items-center p-4 border-b bg-white z-10 flex-shrink-0">
          <h2 className="text-lg font-bold text-slate-800">Detail Data Petugas</h2>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 transition">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </header>

        <div className="flex flex-col lg:flex-row flex-grow min-h-0">
          <ProfileSidebar data={data} activeTab={activeTab} setActiveTab={setActiveTab} />

          <main className="w-full lg:flex-grow bg-slate-50 overflow-y-auto">
            <nav className="sticky top-0 lg:hidden bg-white/70 backdrop-blur-sm border-b z-10">
              <div className="flex items-center justify-start overflow-x-auto">
                {navigationItems.map(item => {
                  const isActive = activeTab === item.id;
                  return (
                    <button key={item.id} onClick={() => setActiveTab(item.id)} className={`relative flex-shrink-0 flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-colors ${isActive ? "text-teal-600" : "text-slate-600 hover:text-teal-600"}`}>
                      <span>{item.label}</span>
                      {isActive && <motion.div layoutId="mobile-active-underline" className="absolute bottom-0 left-0 right-0 h-1 bg-teal-500"/>}
                    </button>
                  )
                })}
              </div>
            </nav>
            
            <div className="p-6 lg:p-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-white rounded-xl border border-slate-200">
                        {activeNavItem.icon && <activeNavItem.icon className="w-6 h-6 text-teal-500" />}
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800">{activeNavItem.label}</h3>
                  </div>

                  {activeTab === 'dokumen' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-6">
                        {activeNavItem.fields.map(fieldKey => (
                           <DocumentCard key={fieldKey} fieldKey={fieldKey} fileUrl={data[fieldKey]} />
                        ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                        {activeNavItem.fields.map(fieldKey => (
                           <DetailItem key={fieldKey} fieldKey={fieldKey} value={data[fieldKey]} />
                        ))}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>

          <aside className="hidden lg:block lg:w-[340px] lg:flex-shrink-0 bg-white border-l p-8 overflow-y-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-teal-50 rounded-lg">
                  <DocumentCheckIcon className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">Dokumen Terlampir</h3>
            </div>
            <div className="space-y-6">
              {documentFields.map(fieldKey => <DocumentCard key={fieldKey} fieldKey={fieldKey} fileUrl={data[fieldKey]} />)}
            </div>
          </aside>
        </div>
      </motion.div>
    </div>
  );
}